<script lang="ts">
	import Actirise, { Mode, PageType } from '$lib/components/Actirise.svelte';
	import TopWideBlock from '$lib/components/ArticleGrid/TopWideBlock.svelte';
	import DefaultOgImage from '$lib/components/DefaultOgImage.svelte';
	import LdTag from '$lib/components/LdTag.svelte';
	import { translations } from '$lib/i18n/translations';
	import { categorySchema } from '$lib/utils/SchemaHelper';
	import type { PageData } from './$types';

	export let data: PageData;

	const property = data.property;
	const colors = property.colors;
	const t = translations(property.language);

	$: articles = data.articles;
	$: category = data.category;
	$: canonical = data.canonical;
	$: isSecondPage = data.isSecondPage;
	$: after = data.after;

	$: title = isSecondPage
		? t('categoryPageSecondPageTitle', { category: category.name })
		: category.name;
	$: description = isSecondPage
		? t('categoryPageSecondPageDescription', { category: category.name, siteName: property.name })
		: t('categoryPageDescription', { category: category.name, siteName: property.name });
</script>

<svelte:head>
	<title>{`${title} - ${property.name}`}</title>
	<link rel="canonical" href={canonical} />
	<meta name="description" content={description} />
	<meta name="keywords" content={t('categoryPageKeywords', { category: category.name })} />

	<meta property="og:title" content={`${title} - ${property.name}`} />
	<meta property="og:description" content={description} />

	<meta property="og:url" content={canonical} />
	<meta property="og:type" content="website" />

	<link
		rel="next"
		href={`https://${property.hostname}/${category.slug}?after=${articles[articles.length - 1]._id}`}
	/>
</svelte:head>

<Actirise mode={Mode.PAGE} type={PageType.CATEGORY} parameters={{ custom1: data.cmp.mode }} />

<LdTag schema={categorySchema(category, articles, property, title, description, after)} />

<DefaultOgImage />

<div class="max-w-screen-lg px-4 xl:px-0 mx-auto">
	<div class="mt-5 mb-3">
		<h1 class="text-2xl font-semibold">
			{isSecondPage ? t('categoryPageSecondPageTitle', { category: category.name }) : category.name}
		</h1>
		<h2 class="vh">
			{isSecondPage
				? t('categoryPageSecondPageHeading', { category: category.name })
				: t('categoryPageHeading', { category: category.name })}
		</h2>
	</div>

	<div class="grid gap-6 grid-cols-1 md:grid-cols-2">
		{#each articles as article, i}
			<TopWideBlock {article} lazy={!(i < 4)} />
		{/each}
	</div>

	{#if articles.length >= 10}
		<div class="mt-5 text-right">
			<a
				href={`?after=${articles[articles.length - 1]._id}`}
				class={`inline-flex items-center group ${colors.text} hover:${colors.hover}`}
			>
				<span class="mr-2">
					<span>{t('seemore')}</span>
					<span
						class={`block max-w-0 group-hover:max-w-full transition-all duration-500 h-0.5 ${colors.hoverBackground}`}
					></span>
				</span>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 16 16"
					fill="currentColor"
					class="w-4 h-4"
				>
					<path
						fill-rule="evenodd"
						d="M2 8a.75.75 0 0 1 .75-.75h8.69L8.22 4.03a.75.75 0 0 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H2.75A.75.75 0 0 1 2 8Z"
						clip-rule="evenodd"
					/>
				</svg>
			</a>
		</div>
	{/if}
</div>
